import React, { useState, useEffect } from 'react';
import api from '../api/client';

const LABEL_INFO = {
  hot: { label: '🔥 Горячие', color: '#e74c3c' },
  warm: { label: '👍 Тёплые', color: '#e67e22' },
  cold: { label: '❄️ Холодные', color: '#3498db' },
  refused: { label: '✗ Отказ', color: '#95a5a6' },
  deal: { label: '✅ Сделки', color: '#27ae60' },
};

function StatCard({ title, value, sub, color }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e8e8e6', borderRadius: 10, padding: '16px 20px', flex: 1, minWidth: 140 }}>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: color || '#222' }}>{value ?? '—'}</div>
      {sub && <div style={{ fontSize: 12, color: '#aaa', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function MiniBar({ value, max, color }) {
  const pct = max > 0 ? Math.round(value / max * 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 8, background: '#f0f0ee', borderRadius: 4 }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 4, transition: 'width 0.4s' }} />
      </div>
      <span style={{ fontSize: 12, color: '#555', minWidth: 28, textAlign: 'right' }}>{value}</span>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [managers, setManagers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/analytics/dashboard'),
      api.get('/analytics/managers').catch(() => ({ data: [] })),
      api.get('/analytics/campaigns').catch(() => ({ data: [] })),
    ]).then(([s, m, c]) => {
      setStats(s.data); setManagers(m.data); setCampaigns(c.data);
      setLoading(false);
    });
  }, []);

  if (loading) return <div style={{ padding: 40, color: '#aaa' }}>Загружаем аналитику...</div>;
  if (!stats) return null;

  const maxDaily = Math.max(...stats.daily.map(d => d.conversations), 1);
  const maxFunnel = Math.max(...Object.values(stats.funnel), 1);

  return (
    <div style={{ padding: 28, maxWidth: 1100 }}>
      <h1 style={{ fontSize: 20, fontWeight: 600, margin: '0 0 24px' }}>Обзор</h1>

      {/* Главные метрики */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
        <StatCard title="Аккаунтов" value={stats.accounts.total} sub={`${stats.accounts.active} активных`} />
        <StatCard title="Диалогов всего" value={stats.conversations.total} sub={`${stats.conversations.today} сегодня`} color="#534AB7" />
        <StatCard title="На эскалации" value={stats.conversations.escalated} sub="ждут оператора" color="#e67e22" />
        <StatCard title="Отправлено рассылок" value={stats.campaigns.sent} sub={`${stats.campaigns.replied} ответили · ${stats.campaigns.conversion}% конверсия`} color="#27ae60" />
        <StatCard title="Сообщений" value={stats.messages.total} sub="всего в диалогах" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        {/* Активность по дням */}
        <div style={{ background: '#fff', border: '1px solid #e8e8e6', borderRadius: 10, padding: 20 }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 16 }}>Активность за 7 дней</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {stats.daily.map(d => (
              <div key={d.date}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#888', marginBottom: 3 }}>
                  <span>{d.date}</span>
                  <span>{d.messages} сообщений</span>
                </div>
                <MiniBar value={d.conversations} max={maxDaily} color="#534AB7" />
              </div>
            ))}
          </div>
        </div>

        {/* Воронка */}
        <div style={{ background: '#fff', border: '1px solid #e8e8e6', borderRadius: 10, padding: 20 }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 16 }}>Воронка диалогов</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {Object.entries(LABEL_INFO).map(([key, info]) => (
              <div key={key}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
                  <span style={{ color: info.color }}>{info.label}</span>
                </div>
                <MiniBar value={stats.funnel[key] || 0} max={maxFunnel} color={info.color} />
              </div>
            ))}
            <div style={{ fontSize: 12, color: '#aaa', marginTop: 4 }}>
              Без метки: {stats.conversations.total - Object.values(stats.funnel).reduce((a, b) => a + b, 0)}
            </div>
          </div>
        </div>
      </div>

      {/* Рассылки */}
      {campaigns.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e8e8e6', borderRadius: 10, padding: 20, marginBottom: 20 }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 14 }}>Рассылки</div>
          <div style={{ display: 'grid', gap: 8 }}>
            {campaigns.map(c => (
              <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid #f5f5f5' }}>
                <div style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>{c.name}</div>
                <div style={{ fontSize: 12, color: '#888' }}>Отправлено: {c.sent}/{c.total}</div>
                <div style={{ fontSize: 12, color: '#27ae60' }}>Ответили: {c.replied}</div>
                <div style={{ fontSize: 12, color: c.conversion > 10 ? '#27ae60' : '#888', fontWeight: 600 }}>{c.conversion}%</div>
                <div style={{ width: 80, height: 6, background: '#f0f0ee', borderRadius: 3 }}>
                  <div style={{ height: '100%', width: `${c.total ? c.sent / c.total * 100 : 0}%`, background: '#534AB7', borderRadius: 3 }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Менеджеры */}
      {managers.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e8e8e6', borderRadius: 10, padding: 20 }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 14 }}>Менеджеры</div>
          <div style={{ display: 'grid', gap: 10 }}>
            {managers.map(m => (
              <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '10px 14px', background: '#f8f8f7', borderRadius: 8 }}>
                <div style={{ fontSize: 20 }}>👤</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500, fontSize: 13 }}>{m.name}</div>
                  <div style={{ fontSize: 12, color: '#aaa' }}>{m.email}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#534AB7' }}>{m.conversations}</div>
                  <div style={{ fontSize: 11, color: '#aaa' }}>диалогов</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#e67e22' }}>{m.escalated}</div>
                  <div style={{ fontSize: 11, color: '#aaa' }}>эскалаций</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#27ae60' }}>{m.deals}</div>
                  <div style={{ fontSize: 11, color: '#aaa' }}>сделок</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
